/**
 * Pièces en attente — `extranet.pendingPiecesListCard` (fiche 22780,
 * `WS2_EA_TAB_PMQ`): the « En attente » section with one design-system
 * `Card` per piece — libellé + statut badge on the first line, then the
 * dossier link (FileText « Contrat … » / AlertTriangle « Sinistre … ») ·
 * « Demandé le JJ/MM/AAAA ».
 *
 * Configurable-ready: each piece renders from the declarative descriptors
 * `PENDING_PIECE_ITEM_FIELDS` (`./pendingPieceItems.ts` — stable keys
 * `libelle`, `statut`, `lienDossier`, `dateDemande`; labels/tooltips in
 * config). `_layoutOverrides` (`items`, else the rule engine's `fields`
 * bucket) renames (the label is the « Demandé le » prefix), hides, reorders
 * (within a line) and tooltips them; `setTitle` renames « En attente ». The
 * base config is published under the registered id.
 *
 * A `null` value renders nothing (fiche rule). No upload zone and no
 * « Déposées récemment » section — both unspecified.
 */

import { Fragment, useMemo, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Icon, Text, Tooltip } from '@components/base/display';
import { Skeleton } from '@components/base/feedback';
import { Card, HStack, VStack } from '@components/base/layout';
import {
  applyItemOverrides,
  buildPublishedConfigFromInfoItems,
  resolveCardTitle,
  usePublishLayoutConfig,
  type LayoutOverrides,
} from '@components/shared/configurable';

import type { PendingPiece } from '../../api/piecesService';
import { CardBodyState, CardTitleRow, PIECE_STATUS_BADGE, StatusBadge } from '../shared';
import {
  PENDING_PIECE_ITEM_FIELDS,
  PENDING_PIECE_ORIGIN_ICON,
  PENDING_PIECES_LIST_CARD_ID,
  PENDING_PIECES_TEXT,
  pendingPieceDossierPath,
  pendingPieceFieldText,
  pendingPieceFieldValue,
  type PendingPieceItemField,
} from './pendingPieceItems';

export interface PendingPiecesListCardProps {
  pieces: readonly PendingPiece[];
  loading?: boolean;
  error?: string | null;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

/** Number of placeholder cards while loading. */
const SKELETON_CARDS = 2;

function sortByOrder(fields: PendingPieceItemField[]): PendingPieceItemField[] {
  return fields.sort(
    (a, b) => (a.order ?? Number.POSITIVE_INFINITY) - (b.order ?? Number.POSITIVE_INFINITY),
  );
}

function LoadingPieces() {
  return (
    <VStack gap={3} data-testid="pending-pieces-loading" aria-busy="true">
      {Array.from({ length: SKELETON_CARDS }, (_, index) => (
        <Card key={index} padding="sm">
          <Card.Content>
            <VStack gap={2}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
            </VStack>
          </Card.Content>
        </Card>
      ))}
    </VStack>
  );
}

interface DossierLinkProps {
  piece: PendingPiece;
  text: string;
  tenantSlug: string | undefined;
}

/** Origin sign + the REFERENCE text, linked to the contract / claim when possible. */
function DossierLink({ piece, text, tenantSlug }: Readonly<DossierLinkProps>) {
  const sign = piece.origine === null ? null : PENDING_PIECE_ORIGIN_ICON[piece.origine];
  const to = pendingPieceDossierPath(piece, tenantSlug);
  const label = (
    <Text as="span" size="xs" color={to === null ? 'muted' : 'primary'}>
      {text}
    </Text>
  );
  return (
    <HStack gap={1} align="center" inline>
      {sign && <Icon icon={sign} size="xs" color="muted" />}
      {to === null ? (
        label
      ) : (
        <Link to={to} className="hover:underline" data-dossier-origin={piece.origine}>
          {label}
        </Link>
      )}
    </HStack>
  );
}

export function PendingPiecesListCard({
  pieces,
  loading = false,
  error = null,
  _layoutOverrides,
}: Readonly<PendingPiecesListCardProps>) {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  usePublishLayoutConfig(
    PENDING_PIECES_LIST_CARD_ID,
    buildPublishedConfigFromInfoItems(
      [...PENDING_PIECE_ITEM_FIELDS],
      PENDING_PIECES_TEXT.sectionTitle,
    ),
  );

  const { primaryFields, secondaryFields } = useMemo(() => {
    const overrides = _layoutOverrides?.items ?? _layoutOverrides?.fields;
    const visible = [...applyItemOverrides(PENDING_PIECE_ITEM_FIELDS, overrides)].filter(
      (field) => field.visible !== false,
    );
    return {
      primaryFields: sortByOrder(visible.filter((field) => field.line === 'primary')),
      secondaryFields: sortByOrder(visible.filter((field) => field.line === 'secondary')),
    };
  }, [_layoutOverrides]);

  const title = resolveCardTitle(PENDING_PIECES_TEXT.sectionTitle, _layoutOverrides);

  const renderValue = (
    field: PendingPieceItemField,
    piece: PendingPiece,
    isFirst: boolean,
  ): ReactNode => {
    switch (field.kind) {
      case 'badge':
        return (
          <StatusBadge value={pendingPieceFieldValue(field, piece)} config={PIECE_STATUS_BADGE} />
        );
      case 'link':
        return (
          <DossierLink
            piece={piece}
            text={pendingPieceFieldText(field, piece)}
            tenantSlug={tenantSlug}
          />
        );
      default: {
        const isPrimary = field.line === 'primary';
        return (
          <Text
            as="span"
            size={isPrimary ? 'sm' : 'xs'}
            weight={isPrimary && isFirst ? 'medium' : 'normal'}
            color={isPrimary ? 'default' : 'muted'}
          >
            {pendingPieceFieldText(field, piece)}
          </Text>
        );
      }
    }
  };

  const renderField = (field: PendingPieceItemField, piece: PendingPiece, isFirst: boolean) => {
    const content = renderValue(field, piece, isFirst);
    return field.tooltip ? (
      <Tooltip key={field.key} content={field.tooltip}>
        <span data-field-key={field.key}>{content}</span>
      </Tooltip>
    ) : (
      <span key={field.key} data-field-key={field.key}>
        {content}
      </span>
    );
  };

  /** The fields of a line that carry a value for this piece (null ⇒ nothing). */
  const withValue = (fields: PendingPieceItemField[], piece: PendingPiece) =>
    fields.filter((field) => pendingPieceFieldValue(field, piece) !== '');

  return (
    <VStack as="section" gap={3} data-testid="pending-pieces-list-card">
      {title !== '' && <CardTitleRow title={title} />}
      {loading ? (
        <LoadingPieces />
      ) : (
        <CardBodyState error={error} empty={pieces.length === 0}>
          {pieces.map((piece) => {
            const primary = withValue(primaryFields, piece);
            const secondary = withValue(secondaryFields, piece);
            return (
              <Card key={piece.id} padding="sm" data-piece-id={piece.id}>
                <Card.Content>
                  <VStack gap={1}>
                    {primary.length > 0 && (
                      <HStack gap={2} align="center" wrap>
                        {primary.map((field, index) => renderField(field, piece, index === 0))}
                      </HStack>
                    )}
                    {secondary.length > 0 && (
                      <HStack gap={2} align="center" wrap>
                        {secondary.map((field, index) => (
                          <Fragment key={field.key}>
                            {index > 0 && (
                              <Text as="span" size="xs" color="muted" aria-hidden="true">
                                {PENDING_PIECES_TEXT.separator}
                              </Text>
                            )}
                            {renderField(field, piece, index === 0)}
                          </Fragment>
                        ))}
                      </HStack>
                    )}
                  </VStack>
                </Card.Content>
              </Card>
            );
          })}
        </CardBodyState>
      )}
    </VStack>
  );
}
